import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ClipboardCheck, Eye, ListChecks, Plus, Settings2 } from 'lucide-react';
import { useCanApproveSuppliers } from '../../hooks/useCanApproveSuppliers';
import AuditActionsTable from './AuditActionsTable';
import AuditList from './AuditList';
import AuditTemplatesDialog from './AuditTemplatesDialog';
import StartAuditDialog from './StartAuditDialog';
import { useAudits } from './useAudits';

/** 'archived' = only archived audits; every other choice leaves them out (server default). */
type StatusFilter = 'all' | 'in_progress' | 'completed' | 'expired' | 'archived';

/**
 * Questionnaires › Audits: the customer's own audits and the actions they raised.
 * `?view=actions` opens the Actions view (`&action=<id>` highlights one).
 */
export default function AuditsPanel() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isCustomer = user?.platform === 'CUSTOMER';
  const canManageTemplates = useCanApproveSuppliers(isCustomer);
  const view = searchParams.get('view') === 'actions' ? 'actions' : 'audits';
  const focusId = Number(searchParams.get('action')) || null;
  const [status, setStatus] = useState<StatusFilter>('all');
  const [auditor, setAuditor] = useState<'all' | 'me'>('all');
  const [search, setSearch] = useState<string>('');
  const [startOpen, setStartOpen] = useState<boolean>(false);
  const [templatesOpen, setTemplatesOpen] = useState<boolean>(false);

  const { audits, loading, error, reload } = useAudits({
    ...(status === 'archived' ? { archived: true } : status !== 'all' ? { status } : {}),
    ...(auditor === 'me' ? { auditor: 'me' as const } : {}),
  });
  const filtered = Boolean(search) || status !== 'all' || auditor !== 'all';
  const emptyText = status === 'archived' && !search && auditor === 'all' ? 'No archived audits.'
    : filtered ? 'No audits match these filters.'
    : (isCustomer ? 'No audits yet. Start an audit to record a factory visit.' : 'No audits yet.');
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return audits;
    return audits.filter((a) => `${a.supplier_name} ${a.name ?? ''} ${a.template_name}`.toLowerCase().includes(needle));
  }, [audits, search]);

  const setView = (next: 'audits' | 'actions') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'actions') params.set('view', 'actions');
    else { params.delete('view'); params.delete('action'); }
    setSearchParams(params);
  };

  return (
    <div className="space-y-4">
      {!isCustomer && (
        <p className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
          <Eye className="h-4 w-4" />Read-only: audits are started and completed by the customer&apos;s own team.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-md border p-0.5">
          <Button size="sm" variant={view === 'audits' ? 'default' : 'ghost'} onClick={() => setView('audits')}>
            <ClipboardCheck className="h-4 w-4 mr-1.5" />Audits
          </Button>
          <Button size="sm" variant={view === 'actions' ? 'default' : 'ghost'} onClick={() => setView('actions')}>
            <ListChecks className="h-4 w-4 mr-1.5" />Actions
          </Button>
        </div>
        {isCustomer && (
          <div className="flex gap-2">
            {canManageTemplates && (
              <Button variant="outline" onClick={() => setTemplatesOpen(true)}>
                <Settings2 className="h-4 w-4 mr-1.5" />Audit templates
              </Button>
            )}
            <Button onClick={() => setStartOpen(true)}><Plus className="h-4 w-4 mr-1.5" />Start audit</Button>
          </div>
        )}
      </div>

      {view === 'audits' ? (
        <>
          <div className="flex flex-wrap gap-3">
            <Input className="max-w-xs" placeholder="Search supplier, audit name or template" value={search}
              onChange={(e) => setSearch(e.target.value)} aria-label="Search audits" />
            <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
              <SelectTrigger className="w-44" aria-label="Status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select value={auditor} onValueChange={(v) => setAuditor(v as 'all' | 'me')}>
              <SelectTrigger className="w-44" aria-label="Lead auditor"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All auditors</SelectItem>
                <SelectItem value="me">Led by me</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <AuditList audits={shown} loading={loading} error={error} onRetry={() => void reload()}
            onChanged={() => void reload()}
            emptyText={emptyText}
            emptyAction={isCustomer && !filtered
              ? <Button onClick={() => setStartOpen(true)}><Plus className="h-4 w-4 mr-1.5" />Start audit</Button>
              : undefined} />
        </>
      ) : (
        <AuditActionsTable initialFilter={focusId ? 'open' : 'mine'} readOnly={!isCustomer} focusId={focusId} />
      )}

      <StartAuditDialog open={startOpen} onOpenChange={setStartOpen} />
      <AuditTemplatesDialog open={templatesOpen} onOpenChange={setTemplatesOpen} />
    </div>
  );
}
